import { CloseButton } from '@orn/shared-web/components/Button';
import { CheckIcon, ClockIcon } from '@orn/shared-web/icons';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import AlertBlockedNotice from '../alerts/AlertBlockedNotice';

export { BLOCKED_NOTICE_TEXT } from '../alerts/AlertBlockedNotice';

export type AlertListItemStatus = 'unread' | 'read' | 'pending' | 'resolved';

interface IAlertListItemProps {
  /** Leading visual, rendered at 40px — caller provides the logo image or tinted icon circle. */
  icon: React.ReactNode;
  /** Headline, truncated to a single line. */
  title: React.ReactNode;
  /**
   * Secondary text, clamped to two lines; callers wrap emphasis in `<strong>`,
   * which the row colors to match its fill.
   * The two-line space is reserved even when shorter or absent, so every row
   * renders at one standard height.
   */
  subtitle?: React.ReactNode;
  /** Preformatted compact timestamp — see `formatCompactTimeAgo` in shared-web. */
  timestamp?: string;
  /**
   * Status badge on the icon's corner: a dot when unread, a clock when pending,
   * a check when resolved. `read` shows nothing. @default 'read'
   */
  status?: AlertListItemStatus;
  /** 'error' renders the title in the error color. @default 'default' */
  tone?: 'default' | 'error';
  /**
   * Brand fill, for a row a surface is holding open — the design's `Active`
   * state. The Notification Center sets it on the alert the assistant
   * transcript is showing.
   */
  selected?: boolean;
  onClick?: () => void;
  /** Renders a dismiss X, revealed on hover/focus over the timestamp. Ignored on a `resolved` row. */
  onDismiss?: () => void;
  /**
   * Refusal feedback for a click the surface would not act on (the assistant
   * is mid-turn, or the household holds no membership): dims the row to 30% and floats a lock pill over it at full
   * opacity, both fading. The row stays interactive — the click is what
   * produces this feedback — so the dismiss X keeps working.
   */
  showBlockedNotice?: boolean;
  /** The lock pill's text; defaults to the mid-turn wording. */
  blockedNoticeText?: string;
  /**
   * `flat` sits on its list's ground, ruled off from the row above by a
   * hairline; `card` is the Figma "Alert Items/Inbox Style" — its own rounded
   * surface, border, and shadow, for a row laid out on its own. @default 'flat'
   */
  variant?: 'flat' | 'card';
  className?: string;
}

/**
 * Vertical list row for the Alerts Center — or, as `variant="card"`, a card in
 * the phone carousel: icon + title + subtitle + compact timestamp, and a
 * status badge on the icon. Every row is one standard height
 * regardless of subtitle length. Purely presentational — callers own
 * data fetching, the status/tone mapping, and per-type icon choice.
 * When `onClick` is set the row is keyboard-actionable
 * (Enter/Space); `onDismiss` reveals an X over the timestamp on hover/focus,
 * except on a `resolved` row, which is already closed.
 */
const AlertListItem = ({
  icon,
  title,
  subtitle,
  timestamp,
  status = 'read',
  tone = 'default',
  selected = false,
  onClick,
  onDismiss,
  showBlockedNotice = false,
  blockedNoticeText,
  variant = 'flat',
  className,
}: IAlertListItemProps) => {
  const titleId = React.useId();
  const isCard = variant === 'card';

  // A resolved alert is closed, so there is nothing left to dismiss — the row
  // keeps its timestamp and drops the X even where a surface hands it a handler.
  const dismiss = status === 'resolved' ? undefined : onDismiss;

  // The badge's cut-out ring takes the unfilled row's ground, and keeps it on a filled row.
  const badgeRingClass = isCard ? 'ring-bg-primary' : 'ring-bg-app_light';

  return (
    // The pill cannot live inside the row — it must stay at full opacity
    // while the row dims — so both hang off this neutral wrapper.
    <div
      data-selected={selected || undefined}
      className={cx(
        'relative w-full',
        // The rule lives out here, not on the card below: this wrapper is what
        // sits beside the other rows, so `first:` means "first row of the
        // group" rather than "first child of my own wrapper". A filled row
        // reads as one block, so neither rule touching it draws — its own, and
        // the one the row after it would draw along the fill's bottom edge.
        !isCard && 'border-secondary border-t first:border-t-0 [[data-selected]+&]:border-t-transparent',
        !isCard && selected && 'border-t-transparent',
        className
      )}
    >
      <div
        className={cx(
          'group relative flex w-full items-start gap-2 rounded-2xl p-3',
          'transition-opacity duration-200',
          // The composer input's surface, which the Figma draws the card in too.
          // 11px inside the border: a Figma stroke takes no layout space, so
          // the content sits 12px from the edge and the card stays 84px tall.
          isCard && 'bg-primary border-secondary gap-3 border p-[11px] shadow-sm',
          // Rows sit flat on the card's ground; the rule between them is on the
          // wrapper above.
          selected && 'bg-brand-solid',
          isCard && selected && 'border-transparent',
          // Only when not filled: a `hover:` variant outranks the plain
          // `bg-brand-solid` above it, so a hovered filled row would lose its fill.
          onClick && !selected && 'hover:bg-primary_hover',
          showBlockedNotice && 'opacity-30'
        )}
      >
        {/*
         * The row action is an overlay button rather than a clickable root, so the
         * dismiss button stays its sibling — a control nested inside a button is
         * not reliably reachable for keyboard and screen-reader users.
         */}
        {onClick && (
          <button
            type="button"
            aria-labelledby={titleId}
            onClick={onClick}
            className="outline-focus-ring absolute inset-0 cursor-pointer rounded-2xl focus-visible:outline-2"
          />
        )}

        <div className="relative shrink-0">
          {icon}
          {/* The ring cuts the badge out of the icon beneath it, which is an arbitrary brand logo. */}
          {status === 'unread' && (
            <span
              className={cx(
                'bg-utility-brand-400 absolute -bottom-1 -right-1 size-3.5 rounded-full ring-2',
                badgeRingClass
              )}
            />
          )}
          {status === 'pending' && (
            <span
              className={cx(
                'bg-utility-warning-400 absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full ring-2',
                badgeRingClass
              )}
            >
              <ClockIcon className="text-fg-white size-2.5 shrink-0" />
            </span>
          )}
          {status === 'resolved' && (
            <span
              className={cx(
                'bg-success-solid absolute -bottom-1 -right-1 flex size-3.5 items-center justify-center rounded-full ring-2',
                badgeRingClass
              )}
            >
              <CheckIcon className="text-fg-white size-2.5 shrink-0" />
            </span>
          )}
        </div>

        {/* No `overflow-hidden` here: the dismiss button sits above this column's
          top edge, and clipping the column clips the button. Overflow is cut on
          the title and subtitle themselves, which the button does not overlap. */}
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {/* The leading is the row's, not just the title's: the timestamp's
              wrapper would otherwise strut to the ambient 24px and outgrow it. */}
          <div className="flex items-start justify-between gap-3 leading-[22px]">
            <span
              id={titleId}
              className={cx(
                // The design's own line height (Figma `Line height/text-md`);
                // the app scale's 24px is what made the row read as padded.
                'text-md min-w-0 truncate font-semibold leading-[22px]',
                selected ? 'text-primary_on-brand' : tone === 'error' ? 'text-error-primary' : 'text-primary'
              )}
            >
              {title}
            </span>
            {(timestamp || dismiss) && (
              <span className="relative shrink-0">
                {timestamp && (
                  <span
                    className={cx(
                      'whitespace-nowrap text-xs',
                      selected ? 'text-quaternary_on-brand' : 'text-quaternary',
                      dismiss && 'group-hover:opacity-0 group-has-[:focus-visible]:opacity-0'
                    )}
                  >
                    {timestamp}
                  </span>
                )}
                {dismiss && (
                  <span
                    className={cx(
                      // `:focus-visible`, not `:focus-within` — a mouse click leaves
                      // DOM focus on the button, which would pin the X open and keep
                      // the timestamp hidden long after the pointer has left.
                      // z-10 lifts the button above the row's overlay action.
                      'z-10 opacity-0 group-hover:opacity-100 group-has-[:focus-visible]:opacity-100',
                      timestamp ? 'absolute -top-1 right-0' : 'relative'
                    )}
                  >
                    <CloseButton
                      size="xs"
                      label="Dismiss alert"
                      onPress={dismiss}
                      className="bg-tertiary rounded-full"
                    />
                  </span>
                )}
              </span>
            )}
          </div>
          {/* min-h-[2lh] holds both subtitle lines even when empty — one standard row height. */}
          <div
            className={cx(
              // `2lh` follows this leading, so the reserve is two design lines.
              'line-clamp-2 min-h-[2lh] break-words text-sm leading-[18px] [&_strong]:font-medium',
              // Emphasis steps up to the title's color, so it has to follow the fill too.
              selected
                ? 'text-secondary_on-brand [&_strong]:text-primary_on-brand'
                : 'text-secondary [&_strong]:text-primary'
            )}
          >
            {subtitle}
          </div>
        </div>
      </div>

      <AlertBlockedNotice show={showBlockedNotice} text={blockedNoticeText} />
    </div>
  );
};

export default AlertListItem;
